import { FC, useMemo, useRef, useState } from 'react';
import { Formik, Form, Field, FormikProps, FieldProps } from 'formik';
import {
  Grid,
  FormHelperText,
  Box,
  Typography,
  List,
  ListItem,
  IconButton,
  ListItemText,
  ListItemSecondaryAction,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import ReactQuill from 'react-quill';
import Dropzone, { FileRejection } from 'react-dropzone';
import { HighlightOff as RejectedFileIcon } from '@material-ui/icons';
import * as yup from 'yup';
import clsx from 'clsx';
import moment, { Moment } from 'moment';
import { SendAtDatePicker } from 'components/SendAtDatePicker';

import { sendEmail as sendEmailApi } from 'http/admin';
import { AlertDialog } from 'components/AlertDialog';
import { useAdmin } from 'store/admin/hooks';
import { CustomLabel, CustomTextField } from 'components/inputs';
import { allowedFileTypes, MAX_FILE_SIZE } from 'core/constants';
import { CompanyDocumentType } from 'core/types';
import { useAuth } from 'store/auth/hooks';
import { Application, CompanyPerson } from 'store/applications/types';
import { CC_DEFAULT_EMAIL_ADDRESS, formats, modules, SIGNATURE_LOGO_SRC } from './constants';
import useStyles from './SendEmailModal.styles';

interface FormValues {
  from: string;
  emails: string;
  cc?: string;
  bcc?: string;
  subject: string;
  body: string;
  send_at: Date | null;
}

interface MultiSendEmailModalProps {
  open: boolean;
  toggleOpen: () => void;
  selectedApplications: Application[];
}

const filesInitialState: { accepted: File[]; rejected: FileRejection[] } = { accepted: [], rejected: [] };

const parseEmails = (value: string): string[] =>
  Array.from(
    new Set(
      value
        .split(',')
        .map((email) => email.trim())
        .filter(Boolean),
    ),
  );

const getApplicantFromApplication = (application: Application): CompanyPerson | undefined => {
  const { director } = application.company;

  return director;
};

const replaceTemplateTokens = (value: string, application: Application): string => {
  const applicant = getApplicantFromApplication(application);

  return value
    .replace(/{{\s*firstName\s*}}/gi, applicant?.first_name ?? 'there')
    .replace(/{{\s*email\s*}}/gi, applicant?.email ?? '')
    .replace(/{{\s*companyName\s*}}/gi, application.company.registered_name ?? '');
};

const MultiSendEmailModal: FC<MultiSendEmailModalProps> = ({ open, toggleOpen, selectedApplications }) => {
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState<{ accepted: File[]; rejected: FileRejection[] }>(filesInitialState);
  const formRef = useRef<FormikProps<FormValues> | null>(null);
  const classes = useStyles();
  const { t } = useTranslation();
  const { setError, setSuccess, setRefreshEmails } = useAdmin();
  const { firstName, lastName, occupation, email: adminEmail } = useAuth();

  const selectedApplicants = useMemo(
    () => selectedApplications.map(({ company: { director } }) => director).filter(Boolean),
    [selectedApplications],
  );

  const selectedEmails = useMemo(
    () => Array.from(new Set(selectedApplicants.map(({ email }) => email?.trim()).filter(Boolean))),
    [selectedApplicants],
  );

  const initialValues: FormValues = useMemo(
    () => ({
      from: adminEmail ?? '',
      emails: selectedEmails.join(', '),
      cc: CC_DEFAULT_EMAIL_ADDRESS,
      bcc: '',
      subject: 'Update on your application for {{companyName}}',
      body: `Hi {{firstName}},
      <br />
      <br />
      <p><strong>${firstName} ${lastName}</strong></p>
      ${occupation ? `<p>${occupation}</p>` : ''}
      <p><img src="${SIGNATURE_LOGO_SRC}" /></p>
      <p>Fair, fast and simple funding</p>
      <br/>
      <p>E: <a href="mailto:${adminEmail}">${adminEmail}</a></p>
      <p>W: <a href="${t('pages.auth.footer.links.websiteUrl')}">${t('pages.auth.footer.links.websiteUrl')}</a></p>
      <p>P: <a href="tel:${t('pages.auth.footer.links.phone')}">${t('pages.auth.footer.links.phone')}</a></p>
      <br/>
      <p>Find out what our <a href="https://uk.trustpilot.com/review/scorethebusiness.com">customers</a> have to say about us.</p>
      <br />
      `,
      send_at: moment().toDate(),
    }),
    [adminEmail, firstName, lastName, occupation, selectedEmails, t],
  );

  const formSchema = useMemo(
    () =>
      yup.object({
        from: yup
          .string()
          .email(t('pages.lead.admin.sendEmail.inputs.from.error'))
          .required(t('pages.lead.admin.sendEmail.inputs.from.required')),
        emails: yup
          .string()
          .required(t('pages.lead.admin.sendEmail.inputs.email.required'))
          .test('comma-separated-emails', t('pages.lead.admin.sendEmail.inputs.email.error'), (value) => {
            if (!value) return false;

            const emails = value
              .split(',')
              .map((email) => email.trim())
              .filter(Boolean);

            if (!emails.length) return false;

            return emails.every((email) => yup.string().email().isValidSync(email));
          }),
        cc: yup
          .string()
          .nullable()
          .notRequired()
          .test({
            name: 'cc-email',
            message: t('pages.lead.admin.sendEmail.inputs.cc.error'),
            test: (value) => !value || yup.string().email().isValidSync(value),
          }),
        bcc: yup
          .string()
          .nullable()
          .notRequired()
          .test({
            name: 'bcc-email',
            message: t('pages.lead.admin.sendEmail.inputs.bcc.error'),
            test: (value) => !value || yup.string().email().isValidSync(value),
          }),
        subject: yup.string().required(t('pages.lead.admin.sendEmail.inputs.subject.required')),
        body: yup
          .string()
          .required(t('pages.lead.admin.sendEmail.inputs.body.required'))
          .min(12, t('pages.lead.admin.sendEmail.inputs.body.required')),
        send_at: yup.date().nullable().required(t('pages.lead.admin.sendEmail.inputs.send_at.required')),
      }),
    [t],
  );

  const buildFormData = (values: FormValues, application: Application, email: string) => {
    const formData = new FormData();

    if (files.accepted.length) {
      formData.append('type', CompanyDocumentType.EMAIL_ATTACHMENTS);

      files.accepted.forEach((file) => {
        formData.append('file', file);
      });
    }

    formData.append('email', email);
    formData.append('subject', replaceTemplateTokens(values.subject, application));
    formData.append('body', replaceTemplateTokens(values.body, application));

    if (values.cc) formData.append('cc', values.cc);
    if (values.bcc) formData.append('bcc', values.bcc);
    if (values.send_at) {
      formData.append('send_at', moment(values.send_at).toISOString());
    }

    return formData;
  };

  const onSubmit = async (values: FormValues) => {
    if (!selectedApplications.length) {
      setError(t('No applications selected'));
      return;
    }

    setLoading(true);

    try {
      const allowedEmails = new Set(parseEmails(values.emails).map((email) => email.toLowerCase()));

      await Promise.all(
        selectedApplications.map(async (application) => {
          const applicant = getApplicantFromApplication(application);
          const email = applicant?.email?.trim();

          if (!email || !allowedEmails.has(email.toLowerCase())) return;

          await sendEmailApi(application.id, buildFormData(values, application, email));
        }),
      );

      setSuccess(t('pages.lead.admin.sendEmail.messages.success') as string);
      setRefreshEmails(true);
      formRef.current?.resetForm();
      setFiles(filesInitialState);
      toggleOpen();
    } catch (error) {
      setError((error as Error)?.message ?? true);
    }

    setLoading(false);
  };

  const onCancel = () => {
    formRef.current?.resetForm();
    setFiles(filesInitialState);
    toggleOpen();
  };

  const onDrop = async (acceptedFiles: File[], fileRejections: FileRejection[]) => {
    setFiles({ accepted: acceptedFiles, rejected: fileRejections });
  };

  const removeAcceptedFile = (fileName: string) => {
    setFiles((prevFiles) => {
      const updatedFiles = [...prevFiles.accepted].filter((file) => file.name !== fileName);
      return { accepted: updatedFiles, rejected: prevFiles.rejected };
    });
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={formSchema}
      onSubmit={onSubmit}
      innerRef={formRef}
      enableReinitialize
    >
      {({ values, touched, errors, handleChange, handleSubmit, setFieldValue }) => (
        <AlertDialog
          open={open}
          dialogContentTitle={t('pages.lead.admin.sendEmail.title')}
          handleCancel={onCancel}
          handleConfirm={handleSubmit}
          confirmButtonTitle={t('pages.lead.admin.sendEmail.buttons.confirm')}
          loading={loading}
        >
          <Form noValidate>
            <Grid container direction="column" spacing={4} className={classes.container}>
              <Grid item>
                <Field
                  id="from"
                  fullWidth
                  component={CustomTextField}
                  name="from"
                  value={values.from}
                  onChange={handleChange}
                  title={t('pages.lead.admin.sendEmail.inputs.from.label')}
                  InputLabelProps={{ shrink: true }}
                  className={classes.textInput}
                  disabled
                />
              </Grid>

              <Grid item>
                <Field
                  id="emails"
                  fullWidth
                  component={CustomTextField}
                  name="emails"
                  value={values.emails}
                  onChange={handleChange}
                  title={t('pages.lead.admin.sendEmail.inputs.email.label')}
                  InputLabelProps={{ shrink: true }}
                  className={classes.textInput}
                  disabled
                />
              </Grid>

              <Grid item>
                <Field
                  id="cc"
                  fullWidth
                  component={CustomTextField}
                  name="cc"
                  value={values.cc}
                  onChange={handleChange}
                  title={t('pages.lead.admin.sendEmail.inputs.cc.label')}
                  InputLabelProps={{ shrink: true }}
                  className={classes.textInput}
                />
              </Grid>

              <Grid item>
                <Field
                  id="bcc"
                  fullWidth
                  component={CustomTextField}
                  name="bcc"
                  value={values.bcc}
                  onChange={handleChange}
                  title={t('pages.lead.admin.sendEmail.inputs.bcc.label')}
                  InputLabelProps={{ shrink: true }}
                  className={classes.textInput}
                />
              </Grid>

              <Grid item>
                <Field
                  id="subject"
                  fullWidth
                  component={CustomTextField}
                  name="subject"
                  value={values.subject}
                  onChange={handleChange}
                  title={t('pages.lead.admin.sendEmail.inputs.subject.label')}
                  InputLabelProps={{ shrink: true }}
                  className={classes.textInput}
                />
              </Grid>

              <Grid item>
                <Field name="send_at" toolbarTitle showToolbar>
                  {({ field, form, meta }: FieldProps) => (
                    <SendAtDatePicker
                      title={t('pages.lead.admin.sendEmail.inputs.send_at.label')}
                      field={field}
                      form={form}
                      meta={meta}
                      onChange={(date: Moment | null) => setFieldValue('send_at', date ?? null)}
                    />
                  )}
                </Field>
              </Grid>

              <Grid item>
                <CustomLabel title={t('pages.lead.admin.sendEmail.inputs.body.label')} />

                <ReactQuill
                  theme="snow"
                  onChange={(value: string) => setFieldValue('body', value)}
                  value={values.body}
                  modules={modules}
                  formats={formats}
                  readOnly={false}
                  preserveWhitespace
                  className={clsx([classes.editor, touched.body && errors.body && classes.editorError])}
                />

                {touched.body && errors.body && <FormHelperText error>{errors.body}</FormHelperText>}
              </Grid>

              <Grid item>
                <CustomLabel title={t('pages.lead.admin.sendEmail.inputs.attachments.label')} />

                <Dropzone
                  onDrop={onDrop}
                  maxSize={MAX_FILE_SIZE}
                  accept={allowedFileTypes.map((fileType) => fileType.mimeType)}
                  multiple
                >
                  {({ getRootProps, getInputProps }) => (
                    <section className={classes.container}>
                      <Grid container spacing={3}>
                        <Grid item xs={12}>
                          <Box {...getRootProps()} className={classes.fileDropOuter}>
                            <Box className={classes.fileDropInner}>
                              <input {...getInputProps()} id="dropDocumentInput-attachment" />

                              <Typography className={classes.dropFilesMessage}>{t('global.uploadDocument')}</Typography>
                            </Box>
                          </Box>
                        </Grid>
                      </Grid>
                    </section>
                  )}
                </Dropzone>

                {files.accepted.length > 0 && (
                  <Box marginTop="15px">
                    <Typography className={classes.sectionTitle}>
                      {t('pages.lead.admin.sendEmail.inputs.attachments.accepted')}
                    </Typography>

                    <List>
                      {files.accepted.map((file: File) => (
                        <ListItem key={file.name} className={classes.listItem}>
                          <ListItemText primary={file.name} className={classes.fileTitle} />
                          <ListItemSecondaryAction>
                            <IconButton
                              edge="end"
                              aria-label="delete"
                              onClick={() => removeAcceptedFile(file.name)}
                              className={classes.deleteButton}
                            >
                              <div className={classes.deleteAvatar}>
                                <RejectedFileIcon color="primary" />
                              </div>
                            </IconButton>
                          </ListItemSecondaryAction>
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}

                {files.rejected.length > 0 && (
                  <Box marginTop="15px">
                    <Typography className={classes.sectionTitle}>
                      {t('pages.lead.admin.sendEmail.inputs.attachments.rejected')}
                    </Typography>

                    <List>
                      {files.rejected.map((file: FileRejection) => (
                        <ListItem key={file.file.name} className={classes.listItem}>
                          <ListItemText primary={file.file.name} className={classes.fileTitle} />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}
              </Grid>
            </Grid>
          </Form>
        </AlertDialog>
      )}
    </Formik>
  );
};

export default MultiSendEmailModal;
